/**
 * LessonLink - ユーティリティ関数
 */

const mainNav = document.getElementById('main-nav') as HTMLElement;
const globalMessage = document.getElementById('global-message') as HTMLElement;

const pageLogin = document.getElementById('page-login') as HTMLElement;
const pageNewPassword = document.getElementById('page-new-password') as HTMLElement;
const pageSchedule = document.getElementById('page-schedule') as HTMLElement;
const pageContact = document.getElementById('page-contact') as HTMLElement;
const pageSettings = document.getElementById('page-settings') as HTMLElement;
const pageUsers = document.getElementById('page-users') as HTMLElement;
const pageSummary = document.getElementById('page-summary') as HTMLElement;

export const pages = { pageLogin, pageNewPassword, pageSchedule, pageContact, pageSettings, pageUsers, pageSummary };

export function showMessage(message: string, type: 'success' | 'error' = 'error') {
  globalMessage.textContent = message;
  globalMessage.className = `mb-4 p-3 rounded ${
    type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
  }`;
  setTimeout(() => {
    globalMessage.focus();
  }, 100);
}

export function clearMessage() {
  globalMessage.textContent = '';
  globalMessage.className = 'mb-4';
}

function hideAllPages() {
  pageLogin.classList.add('hidden');
  pageNewPassword.classList.add('hidden');
  pageSchedule.classList.add('hidden');
  pageContact.classList.add('hidden');
  pageSettings.classList.add('hidden');
  pageUsers.classList.add('hidden');
  pageSummary.classList.add('hidden');
}

export function showPage(page: HTMLElement) {
  hideAllPages();
  page.classList.remove('hidden');
  clearMessage();
  const heading = page.querySelector('h1, h2');
  if (heading) {
    (heading as HTMLElement).setAttribute('tabindex', '-1');
    (heading as HTMLElement).focus();
  }
}

export function setNavVisible(visible: boolean) {
  if (visible) {
    mainNav.classList.remove('hidden');
  } else {
    mainNav.classList.add('hidden');
  }
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * ISO日時を「3月15日（土）14:00〜14:30」形式にフォーマット
 */
export function formatLessonDate(isoDate: string, durationMinutes: number = 30): string {
  const d = new Date(isoDate);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const weekday = WEEKDAYS[d.getDay()];
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');

  const endDate = new Date(d.getTime() + durationMinutes * 60 * 1000);
  const endHours = endDate.getHours().toString().padStart(2, '0');
  const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

  return `${month}月${day}日（${weekday}）${hours}:${minutes}〜${endHours}:${endMinutes}`;
}

/**
 * 削除確認用の短い日付フォーマット「3月15日（土）14:00」
 */
export function formatLessonDateShort(isoDate: string): string {
  const d = new Date(isoDate);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const weekday = WEEKDAYS[d.getDay()];
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${month}月${day}日（${weekday}）${hours}:${minutes}`;
}

/**
 * 記入状況テキストを生成
 */
export function formatNoteStatus(instructorNote: string | null | undefined, studentNote: string | null | undefined): { instructor: string; student: string } {
  const hasInstructor = !!(instructorNote && instructorNote.trim());
  const hasStudent = !!(studentNote && studentNote.trim());
  return {
    instructor: hasInstructor ? '記入済み ✓' : '未記入',
    student: hasStudent ? '記入済み ✓' : '未記入',
  };
}

/**
 * メールアドレスからデフォルト表示名を生成
 */
export function getDefaultDisplayName(email: string): string {
  if (!email || !email.includes('@')) return email || '';
  return email.split('@')[0];
}

/**
 * 通知メールアドレスのバリデーション
 */
export function validateNotificationEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * 通知メールアドレスを追加可能か判定
 */
export function canAddNotificationEmail(currentCount: number): boolean {
  return currentCount < 5;
}

/**
 * ロールが講師か判定
 */
export function isInstructor(role: string | null | undefined): boolean {
  return role === 'instructor';
}

/**
 * 講師欄を編集できるか判定
 */
export function canEditInstructorNote(role: string | null | undefined): boolean {
  return role === 'instructor';
}

/**
 * 生徒欄を編集できるか判定
 */
export function canEditStudentNote(role: string | null | undefined): boolean {
  return role === 'student';
}

const DEFAULT_PRICE = 1200;
const DEFAULT_DURATION = 30;

/**
 * 当月の実施済み授業回数を計算
 */
export function countMonthlyLessons(
  lessons: { date: string }[],
  now: Date,
): number {
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthStart = new Date(year, month, 1);
  return lessons.filter((l) => {
    const d = new Date(l.date);
    return d >= monthStart && d < now;
  }).length;
}

/**
 * 月別料金を計算
 */
export function calculateMonthlyBilling(
  lessons: { date: string }[],
  pricePerLesson: number | null | undefined,
  now: Date,
): { count: number; price: number; total: number } {
  const price = pricePerLesson ?? DEFAULT_PRICE;
  const count = countMonthlyLessons(lessons, now);
  return { count, price, total: count * price };
}

/**
 * 授業時間のデフォルト値を取得
 */
export function getEffectiveDuration(duration: number | null | undefined): number {
  return duration ?? DEFAULT_DURATION;
}

/**
 * 金額をカンマ区切りで表示
 */
export function formatCurrency(amount: number): string {
  return amount.toLocaleString('ja-JP');
}
